/**
 * Convert free-form input into Pagefind's exact-token syntax.
 * Quoting each token keeps word order flexible while excluding stemmed/partial matches.
 *
 * @param {string} term
 * @return {string}
 */
const getSearchTokens = (term) => term.match(/[\p{L}\p{N}]+(?:['’.-][\p{L}\p{N}]+)*/gu) || [];
const hasSearchTokens = (term) => getSearchTokens(term).length > 0;

const buildExactQuery = (term) => {
  const tokens = getSearchTokens(term);

  return tokens.map((token) => `"${token}"`).join(' ');
};

/**
 * Check whether every query token is present as a whole word in title/H1 metadata.
 *
 * @param {string} term
 * @param {string} semanticText
 * @return {boolean}
 */
const hasExactSemanticMatch = (term, semanticText) => {
  const queryTokens = getSearchTokens(term).map((token) => token.toLocaleLowerCase());
  const semanticTokens = new Set(getSearchTokens(semanticText).map((token) => token.toLocaleLowerCase()));

  return queryTokens.length > 0 && queryTokens.every((token) => semanticTokens.has(token));
};

/**
 * Merge Pagefind result groups in priority order and remove duplicates.
 *
 * @param {...Object} searchGroups
 * @return {Object}
 */
const mergeSearchResults = (...searchGroups) => {
  const seen = new Set();
  const results = searchGroups.flatMap((group) => group?.results || []).filter((result) => {
    if (!result?.id || seen.has(result.id)) return false;

    seen.add(result.id);
    return true;
  });
  const fallbackResults = searchGroups[searchGroups.length - 1] || {};

  return {
    ...fallbackResults,
    results,
  };
};

/**
 * Escape Pagefind metadata before inserting it into a result template.
 * Pagefind excerpts are already encoded separately and intentionally retain <mark> tags.
 *
 * @param {*} value
 * @return {string}
 */
const escapeHtml = (value) => String(value ?? '')
  .replaceAll('&', '&amp;')
  .replaceAll('<', '&lt;')
  .replaceAll('>', '&gt;')
  .replaceAll('"', '&quot;')
  .replaceAll('\'', '&#039;');

/**
 * Ranks search results by relevance to the query term:
 * 1. Strict exact title match (e.g. title is literally "Casepoint eDiscovery")
 * 2. Exact phrase match in title or H1
 * 3. All query tokens present in title or H1
 * 4. Remaining results preserving original order
 *
 * @param {string} term
 * @param {Array} topResults
 * @param {Array} topData
 * @return {Array}
 */
const rankSemanticMatches = (term, topResults, topData) => {
  const normTerm = term.trim().toLowerCase();
  const queryTokens = getSearchTokens(term);
  const canUseExactPhrase = queryTokens.length > 0;
  const exactTitleMatches = [];
  const phraseMatches = [];
  const tokenMatches = [];
  const otherResults = [];

  topResults.forEach((result, index) => {
    const meta = topData[index]?.meta || {};
    const title = (meta.title || '').trim().toLowerCase();
    const h1 = (meta.h1 || '').trim().toLowerCase();

    if (canUseExactPhrase && title && title === normTerm) {
      exactTitleMatches.push(result);
    } else if (
      canUseExactPhrase &&
      ((title && title.includes(normTerm)) || (h1 && h1.includes(normTerm)))
    ) {
      phraseMatches.push(result);
    } else if (hasExactSemanticMatch(term, `${meta.title || ''} ${meta.h1 || ''}`)) {
      tokenMatches.push(result);
    } else {
      otherResults.push(result);
    }
  });

  return [
    ...exactTitleMatches,
    ...phraseMatches,
    ...tokenMatches,
    ...otherResults,
  ];
};

module.exports = {
  buildExactQuery,
  escapeHtml,
  getSearchTokens,
  hasExactSemanticMatch,
  hasSearchTokens,
  mergeSearchResults,
  rankSemanticMatches,
};
